/** Reusable record-level audit history (who / when / field old → new). */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, History, ChevronDown, ChevronRight } from "lucide-react";

export const fmtVal = (v) => (v == null || v === "" ? "—" : typeof v === "object" ? JSON.stringify(v).slice(0, 120) : String(v).slice(0, 160));
export const fmtAt = (iso) => { if (!iso) return "—"; const d = new Date(iso); return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }); };
export const ACTION_CLS = { CREATE: "bg-emerald-50 text-emerald-700 border-emerald-200", UPDATE: "bg-sky-50 text-sky-700 border-sky-200", DELETE: "bg-rose-50 text-rose-700 border-rose-200", DUPLICATE: "bg-violet-50 text-violet-700 border-violet-200", EXPORT: "bg-slate-50 text-slate-600 border-slate-200" };

export function ChangeList({ changes, max = 8, testId }) {
  const [all, setAll] = useState(false);
  if (!changes?.length) return <span className="text-[11px] text-slate-400">no field-level diff</span>;
  const rows = all ? changes : changes.slice(0, max);
  return (
    <div className="space-y-0.5" data-testid={testId}>
      {rows.map((c, i) => (
        <div key={i} className="flex flex-wrap items-baseline gap-x-1.5 text-[11px] leading-snug">
          <span className="font-mono text-slate-800">{c.field}</span>
          {c.tags?.length > 0 && <span className="rounded bg-amber-50 border border-amber-200 px-1 font-mono text-[9px] text-amber-700">{c.tags.join(" · ")}</span>}
          <span className="text-rose-600 line-through decoration-rose-300 break-all">{fmtVal(c.old)}</span>
          <span className="text-slate-400">→</span>
          <span className="text-emerald-700 font-medium break-all">{fmtVal(c.new)}</span>
        </div>
      ))}
      {changes.length > max && <button type="button" onClick={() => setAll((a) => !a)} className="text-[11px] text-sky-700 hover:underline">{all ? "Show fewer" : `+${changes.length - max} more fields`}</button>}
    </div>
  );
}

export function AuditEntry({ e, compact }) {
  const [open, setOpen] = useState(!compact);
  return (
    <div className="rounded-md border border-slate-200 bg-white" data-testid="audit-entry">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-center gap-2 px-2.5 py-1.5 text-left text-[12px]">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
        <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] font-bold ${ACTION_CLS[e.action] || ACTION_CLS.EXPORT}`}>{e.action}</span>
        <span className="font-mono text-slate-700">{fmtAt(e.at)}</span>
        <span className="text-slate-800 font-medium truncate">{e.user_email || "system"}</span>
        {e.role && <span className="text-[10px] text-slate-400">{e.role}</span>}
        <span className="ml-auto text-[10px] text-slate-500">{e.change_count ?? e.changes?.length ?? 0} field{(e.change_count ?? e.changes?.length) === 1 ? "" : "s"}{e.reason_missing && <span className="ml-1 text-amber-600">· reason missing</span>}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-2.5 py-2 space-y-1.5">
          <ChangeList changes={e.changes} />
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-400 font-mono">
            {e.reason && <span className="text-slate-600 font-sans">Reason: {e.reason}</span>}
            {e.ip && <span>IP {e.ip}</span>}{e.session_id && <span>session {String(e.session_id).slice(0, 8)}</span>}{e.path && <span>{e.method} {e.path}</span>}{e.source && <span>{e.source}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RecordHistory({ collection, recordId, title = "Change history", compact = true, limit = 50 }) {
  const [data, setData] = useState(null);
  const [show, setShow] = useState(!compact);
  useEffect(() => {
    if (!collection || !recordId || !show) return;
    setData(null);
    api.get(`/audit-trail/record/${collection}/${recordId}`, { params: { limit } }).then(({ data: d }) => setData(d)).catch(() => setData({ items: [], count: 0 }));
  }, [collection, recordId, show, limit]);
  return (
    <section className="space-y-1.5" data-testid="record-history">
      <button type="button" onClick={() => setShow((s) => !s)} className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-800" data-testid="record-history-toggle">
        <History className="h-3.5 w-3.5" />{title}{data ? ` · ${data.count}` : ""}{show ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {show && !data && <div className="flex items-center gap-1 text-[12px] text-slate-400"><Loader2 className="h-3 w-3 animate-spin" />Loading…</div>}
      {show && data?.items.length === 0 && <div className="text-[12px] text-slate-400" data-testid="record-history-empty">No recorded changes yet.</div>}
      {show && data?.items.map((e, i) => <AuditEntry key={`${e.source}-${e.id || e.at}-${i}`} e={e} compact />)}
    </section>
  );
}
